import * as types from './transactions.types';
import { Dispatch } from 'react';
import { notificationErr } from 'views/components/notification';
import { transactionsError } from 'utils/errorCodes';
import { ITranslate } from 'interfaces/general.interface';
import { IuseTableColumnSearchV2PropsSearchItems } from 'hooks/useTableColumnSearchV2';
import { DropResult } from 'react-beautiful-dnd';
import { ITableColumnsSelectOrderState } from 'interfaces/tables.interface';
import { TtransactionsDispatchReducer } from './transactions.reducer';
import { IInitialTransactionsState } from 'interfaces/transactions.interface';
import {
  IgetTransactionsAPIBody,
  IgetDeliveryNotesData,
  IgetDeliveryNotesDataItem,
} from 'api/endpoints/transactions/transactions.response.interface';
import {
  getBillsColumnsAPI,
  getBillsItemsAPI,
  getDeliveryNotesColumnsAPI,
  getDeliveryNotesItemsAPI,
  getOrdersColumnsAPI,
  getOrdersItemsAPI,
} from 'api/endpoints/transactions/transactions.api';

// DELIVERY NOTES ///////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export async function getDeliveryNotesItemsAction({
  dispatch,
  deliveryNotesBodyFetch,
  brandId,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  deliveryNotesBodyFetch: IgetTransactionsAPIBody;
  brandId: string;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  deliveryNotesBodyFetch.filter['Brand ID'].value = brandId;
  const getDeliveryNotes = await getDeliveryNotesData({
    dispatch,
    deliveryNotesBodyFetch,
    signal,
    token,
  });
  if (!getDeliveryNotes) {
    notificationErr(transactionsError.getDeliveryNotesItems_001, translate);
    return false;
  }
  return true;
}

export async function getDeliveryNotesColumnsAction({
  dispatch,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const getDeliveryNotesFetch = await getDeliveryNotesColumnsAPI(token);
    if (getDeliveryNotesFetch.response.status === 200) {
      const columnsOrder: Record<string, ITableColumnsSelectOrderState> = {};
      getDeliveryNotesFetch.data.forEach((column: string, index: number) => {
        if (
          column === 'Tenant ID' ||
          column === 'Client ID' ||
          column === 'Brand ID' ||
          column === 'Source file' ||
          column === 'Document URL'
        ) {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        } else if (
          column === 'Date' ||
          column === 'Document number' ||
          column === 'Client code' ||
          column === 'Client name' ||
          column === 'Agent name' ||
          column === 'Total net amount' ||
          column === 'Total gross amount'
          // ||
          // column === 'Creation Date' ||
          // column === 'Update Date'
        ) {
          columnsOrder[column] = {
            show: true,
            required: false,
            order: index,
          };
        } else {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        }
      });
      dispatch({
        type: types.SET_DELIVERY_NOTES_COLUMNS_ORDER,
        payload: columnsOrder,
      });
      return true;
    }
    notificationErr(transactionsError.getDeliveryNotesColumns_002, translate);
    return false;
  } catch (err) {
    notificationErr(transactionsError.getDeliveryNotesColumns_001, translate);
    return false;
  }
}

export async function searchOnDeliveryNotesItemsAction({
  dispatch,
  catalogBodyFetch,
  searchOnCatalogItemsProps,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  catalogBodyFetch: IgetTransactionsAPIBody;
  searchOnCatalogItemsProps: IuseTableColumnSearchV2PropsSearchItems;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (searchOnCatalogItemsProps.type === 'add') {
    if (searchOnCatalogItemsProps.searchType === '%ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === '%ILIKE') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NOT NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NOT NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'IN',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'NOT IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'NOT IN',
        },
      };
    } else {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: searchOnCatalogItemsProps.searchType,
        },
      };
    }
  } else {
    delete catalogBodyFetch.filter[searchOnCatalogItemsProps.searchField];
  }
  const getCatalog = await getDeliveryNotesData({
    dispatch,
    deliveryNotesBodyFetch: catalogBodyFetch,
    signal,
    token,
  });

  if (!getCatalog) {
    notificationErr(transactionsError.searchOnDeliveryNotesItemsAction_001, translate);
    return false;
  }
  return true;
}

export async function handleDeliveryNotesPaginationAction({
  dispatch,
  page,
  pageSize,
  transactionsState,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TtransactionsDispatchReducer>;
  page: number;
  pageSize: number;
  transactionsState: IInitialTransactionsState;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}) {
  transactionsState.deliveryNotesBodyFetch.index =
    page === 1 ? 0 : page * pageSize - pageSize;
  transactionsState.deliveryNotesBodyFetch.limit = pageSize;
  const getCatalog = await getDeliveryNotesData({
    dispatch,
    deliveryNotesBodyFetch: transactionsState.deliveryNotesBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(transactionsError.handleDeliveryNotesPaginationAction_001, translate);
    return false;
  }
  return true;
}

export function deliveryNotesMoveColumnsOrderAction({
  dispatch,
  result,
  deliveryNotesColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  result: DropResult;
  deliveryNotesColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  if (!result.destination) return;
  const items = Object.entries(deliveryNotesColumnsOrder);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);
  const updatedItems = items.map(([key, column], index) => {
    return [key, { ...column, order: index }];
  });
  dispatch({
    type: types.SET_DELIVERY_NOTES_COLUMNS_ORDER,
    payload: Object.fromEntries(updatedItems),
  });
}

export function deliveryNotesCheckColumnAction({
  dispatch,
  columnKey,
  deliveryNotesColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  columnKey: string;
  deliveryNotesColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  dispatch({
    type: types.SET_DELIVERY_NOTES_COLUMNS_ORDER,
    payload: {
      ...deliveryNotesColumnsOrder,
      [columnKey]: {
        ...deliveryNotesColumnsOrder[columnKey],
        show: !deliveryNotesColumnsOrder[columnKey].show,
      },
    },
  });
}

export async function getDeliveryNotesData({
  dispatch,
  deliveryNotesBodyFetch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  deliveryNotesBodyFetch: IgetTransactionsAPIBody;
  signal: AbortSignal;
  token: string;
}): Promise<boolean> {
  let values: IgetDeliveryNotesData = {
    count: 0,
    total: 0,
    data: [] as Array<IgetDeliveryNotesDataItem>,
  };
  try {
    dispatch({ type: types.SET_IS_LOADING_DELIVERY_NOTES_ITEMS, payload: true });
    const deliveryNotesFetch = await getDeliveryNotesItemsAPI(
      deliveryNotesBodyFetch,
      signal,
      token
    );

    if (deliveryNotesFetch.response.status === 200) {
      for (const item of deliveryNotesFetch.data.data) {
        const transformItem: IgetDeliveryNotesDataItem = {
          ...item,
          key: `${item.ID}`,
        };
        values.data.push(transformItem);
      }
      values.count = deliveryNotesFetch.data.count;
      values.total = deliveryNotesFetch.data.total;
      dispatch({ type: types.SET_DELIVERY_NOTES_ITEMS, payload: values ?? [] });
      return true;
    }
    if (deliveryNotesFetch.response.status === 404) return true;
    return false;
  } catch (err) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_DELIVERY_NOTES_ITEMS, payload: false });
  }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// BILLS ///////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export async function getBillsItemsAction({
  dispatch,
  billsBodyFetch,
  brandId,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  billsBodyFetch: IgetTransactionsAPIBody;
  brandId: string;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  billsBodyFetch.filter['Brand ID'].value = brandId;
  const getBills = await getBillsData({
    dispatch,
    billsBodyFetch,
    signal,
    token,
  });
  if (!getBills) {
    notificationErr(transactionsError.getBillsItems_001, translate);
    return false;
  }
  return true;
}

export async function getBillsColumnsAction({
  dispatch,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const getBillsFetch = await getBillsColumnsAPI(token);
    if (getBillsFetch.response.status === 200) {
      const columnsOrder: Record<string, ITableColumnsSelectOrderState> = {};
      getBillsFetch.data.forEach((column: string, index: number) => {
        if (
          column === 'Tenant ID' ||
          column === 'Client ID' ||
          column === 'Brand ID' ||
          column === 'Source file' ||
          column === 'Document URL'
        ) {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        } else if (
          column === 'Date' ||
          column === 'Document number' ||
          column === 'Client code' ||
          column === 'Client name' ||
          column === 'Agent name' ||
          column === 'Total net amount' ||
          column === 'Total gross amount' ||
          column === 'Creation Date' ||
          column === 'Update Date'
        ) {
          columnsOrder[column] = {
            show: true,
            required: false,
            order: index,
          };
        } else {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        }
      });
      dispatch({
        type: types.SET_BILLS_COLUMNS_ORDER,
        payload: columnsOrder,
      });
      return true;
    }
    notificationErr(transactionsError.getBillsColumns_002, translate);
    return false;
  } catch (err) {
    notificationErr(transactionsError.getBillsColumns_001, translate);
    return false;
  }
}

export async function searchOnBillsItemsAction({
  dispatch,
  catalogBodyFetch,
  searchOnCatalogItemsProps,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  catalogBodyFetch: IgetTransactionsAPIBody;
  searchOnCatalogItemsProps: IuseTableColumnSearchV2PropsSearchItems;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (searchOnCatalogItemsProps.type === 'add') {
    if (searchOnCatalogItemsProps.searchType === '%ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === '%ILIKE') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NOT NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NOT NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'IN',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'NOT IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'NOT IN',
        },
      };
    } else {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: searchOnCatalogItemsProps.searchType,
        },
      };
    }
  } else {
    delete catalogBodyFetch.filter[searchOnCatalogItemsProps.searchField];
  }
  const getBills = await getBillsData({
    dispatch,
    billsBodyFetch: catalogBodyFetch,
    signal,
    token,
  });

  if (!getBills) {
    notificationErr(transactionsError.searchOnBillsItemsAction_001, translate);
    return false;
  }
  return true;
}

export async function handleBillsPaginationAction({
  dispatch,
  page,
  pageSize,
  transactionsState,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TtransactionsDispatchReducer>;
  page: number;
  pageSize: number;
  transactionsState: IInitialTransactionsState;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}) {
  transactionsState.billsBodyFetch.index = page === 1 ? 0 : page * pageSize - pageSize;
  transactionsState.billsBodyFetch.limit = pageSize;
  const getBills = await getBillsData({
    dispatch,
    billsBodyFetch: transactionsState.billsBodyFetch,
    signal,
    token,
  });
  if (!getBills) {
    notificationErr(transactionsError.handleBillsPaginationAction_001, translate);
    return false;
  }
  return true;
}

export function billsMoveColumnsOrderAction({
  dispatch,
  result,
  billsColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  result: DropResult;
  billsColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  if (!result.destination) return;
  const items = Object.entries(billsColumnsOrder);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);
  const updatedItems = items.map(([key, column], index) => {
    return [key, { ...column, order: index }];
  });
  dispatch({
    type: types.SET_BILLS_COLUMNS_ORDER,
    payload: Object.fromEntries(updatedItems),
  });
}

export function billsCheckColumnAction({
  dispatch,
  columnKey,
  billsColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  columnKey: string;
  billsColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  dispatch({
    type: types.SET_BILLS_COLUMNS_ORDER,
    payload: {
      ...billsColumnsOrder,
      [columnKey]: {
        ...billsColumnsOrder[columnKey],
        show: !billsColumnsOrder[columnKey].show,
      },
    },
  });
}

export async function getBillsData({
  dispatch,
  billsBodyFetch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  billsBodyFetch: IgetTransactionsAPIBody;
  signal: AbortSignal;
  token: string;
}): Promise<boolean> {
  let values: IgetDeliveryNotesData = {
    count: 0,
    total: 0,
    data: [] as Array<IgetDeliveryNotesDataItem>,
  };
  try {
    dispatch({ type: types.SET_IS_LOADING_BILLS_ITEMS, payload: true });
    const billsFetch = await getBillsItemsAPI(billsBodyFetch, signal, token);

    if (billsFetch.response.status === 200) {
      for (const item of billsFetch.data.data) {
        const transformItem: IgetDeliveryNotesDataItem = {
          ...item,
          key: `${item.ID}`,
        };
        values.data.push(transformItem);
      }
      values.count = billsFetch.data.count;
      values.total = billsFetch.data.total;
      dispatch({ type: types.SET_BILLS_ITEMS, payload: values ?? [] });
      return true;
    }
    if (billsFetch.response.status === 404) return true;
    return false;
  } catch (err) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_BILLS_ITEMS, payload: false });
  }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// ORDERS ///////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export async function getOrdersItemsAction({
  dispatch,
  ordersBodyFetch,
  brandId,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  ordersBodyFetch: IgetTransactionsAPIBody;
  brandId: string;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  ordersBodyFetch.filter['Brand ID'].value = brandId;
  const getOrders = await getOrdersData({
    dispatch,
    ordersBodyFetch,
    signal,
    token,
  });
  if (!getOrders) {
    notificationErr(transactionsError.getOrdersItems_001, translate);
    return false;
  }
  return true;
}

export async function getOrdersColumnsAction({
  dispatch,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const getOrdersFetch = await getOrdersColumnsAPI(token);
    if (getOrdersFetch.response.status === 200) {
      const columnsOrder: Record<string, ITableColumnsSelectOrderState> = {};
      getOrdersFetch.data.forEach((column: string, index: number) => {
        if (
          column === 'Tenant ID' ||
          column === 'Client ID' ||
          column === 'Brand ID' ||
          column === 'Source file' ||
          column === 'Document URL'
        ) {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        } else if (
          column === 'Date' ||
          column === 'Document number' ||
          column === 'Client code' ||
          column === 'Client name' ||
          column === 'Agent name' ||
          column === 'Total net amount' ||
          column === 'Total gross amount' ||
          column === 'Creation Date' ||
          column === 'Update Date'
        ) {
          columnsOrder[column] = {
            show: true,
            required: false,
            order: index,
          };
        } else {
          columnsOrder[column] = {
            show: false,
            required: false,
            order: index,
          };
        }
      });
      dispatch({
        type: types.SET_ORDERS_COLUMNS_ORDER,
        payload: columnsOrder,
      });
      return true;
    }
    notificationErr(transactionsError.getOrdersColumns_002, translate);
    return false;
  } catch (err) {
    notificationErr(transactionsError.getOrdersColumns_001, translate);
    return false;
  }
}

export async function searchOnOrdersItemsAction({
  dispatch,
  catalogBodyFetch,
  searchOnCatalogItemsProps,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  catalogBodyFetch: IgetTransactionsAPIBody;
  searchOnCatalogItemsProps: IuseTableColumnSearchV2PropsSearchItems;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (searchOnCatalogItemsProps.type === 'add') {
    if (searchOnCatalogItemsProps.searchType === '%ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === '%ILIKE') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NOT NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NOT NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'IN',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'NOT IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'NOT IN',
        },
      };
    } else {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: searchOnCatalogItemsProps.searchType,
        },
      };
    }
  } else {
    delete catalogBodyFetch.filter[searchOnCatalogItemsProps.searchField];
  }
  const getOrders = await getOrdersData({
    dispatch,
    ordersBodyFetch: catalogBodyFetch,
    signal,
    token,
  });

  if (!getOrders) {
    notificationErr(transactionsError.searchOnOrdersItemsAction_001, translate);
    return false;
  }
  return true;
}

export async function handleOrdersPaginationAction({
  dispatch,
  page,
  pageSize,
  transactionsState,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TtransactionsDispatchReducer>;
  page: number;
  pageSize: number;
  transactionsState: IInitialTransactionsState;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}) {
  transactionsState.ordersBodyFetch.index = page === 1 ? 0 : page * pageSize - pageSize;
  transactionsState.ordersBodyFetch.limit = pageSize;
  const getOrders = await getOrdersData({
    dispatch,
    ordersBodyFetch: transactionsState.ordersBodyFetch,
    signal,
    token,
  });
  if (!getOrders) {
    notificationErr(transactionsError.handleOrdersPaginationAction_001, translate);
    return false;
  }
  return true;
}

export function ordersMoveColumnsOrderAction({
  dispatch,
  result,
  ordersColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  result: DropResult;
  ordersColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  if (!result.destination) return;
  const items = Object.entries(ordersColumnsOrder);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);
  const updatedItems = items.map(([key, column], index) => {
    return [key, { ...column, order: index }];
  });
  dispatch({
    type: types.SET_ORDERS_COLUMNS_ORDER,
    payload: Object.fromEntries(updatedItems),
  });
}

export function ordersCheckColumnAction({
  dispatch,
  columnKey,
  ordersColumnsOrder,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  columnKey: string;
  ordersColumnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  dispatch({
    type: types.SET_ORDERS_COLUMNS_ORDER,
    payload: {
      ...ordersColumnsOrder,
      [columnKey]: {
        ...ordersColumnsOrder[columnKey],
        show: !ordersColumnsOrder[columnKey].show,
      },
    },
  });
}

export async function getOrdersData({
  dispatch,
  ordersBodyFetch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TtransactionsDispatchReducer>;
  ordersBodyFetch: IgetTransactionsAPIBody;
  signal: AbortSignal;
  token: string;
}): Promise<boolean> {
  let values: IgetDeliveryNotesData = {
    count: 0,
    total: 0,
    data: [] as Array<IgetDeliveryNotesDataItem>,
  };
  try {
    dispatch({ type: types.SET_IS_LOADING_ORDERS_ITEMS, payload: true });
    const ordersFetch = await getOrdersItemsAPI(ordersBodyFetch, signal, token);

    if (ordersFetch.response.status === 200) {
      for (const item of ordersFetch.data.data) {
        const transformItem: IgetDeliveryNotesDataItem = {
          ...item,
          key: `${item.ID}`,
        };
        values.data.push(transformItem);
      }
      values.count = ordersFetch.data.count;
      values.total = ordersFetch.data.total;
      dispatch({ type: types.SET_ORDERS_ITEMS, payload: values ?? [] });
      return true;
    }
    if (ordersFetch.response.status === 404) return true;
    return false;
  } catch (err) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_ORDERS_ITEMS, payload: false });
  }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
